import {
  CompType,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyData,
  SourceType,
  UnitRateModels,
  defaultUnitRateModelFeatureKeys,
  getRegressionModelUnreliableReason,
  propertyHasCustomManualAdjustments,
} from '@aloft/toolkit-shared';
import {
  Flex,
  FlexProps,
  InfoIcon,
  InfoIconProps,
  ProductColor,
  Tooltip,
  Typography,
  TypographyChildren,
  TypographyProps,
  theme,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { Badge, SystemStyleObject } from '@aloft/toolkit-sui/chakra';

import { PropsWithChildren } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import {
  selectCompType,
  selectEffectiveDate,
  selectHasArvOption,
  selectSalesComparisonApproach,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectCompTypeVisibility } from '../../../redux/features/selectors/propertyDataSelectors/compTypeSelectors.ts';
import { selectPropertyDataById } from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { ResetAdjustmentsButton } from '../ResetAdjustmentsButton.tsx';
import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { PropertyGridForm, PropertyGridType } from '../types.ts';
import { getDataSourceDisplayInfo, getSimilarityScore } from '../utils.ts';

type PropertyGridColumnSubHeaderProps = {
  gridType: PropertyGridType;
  columnIndex: number;
  control: Control<PropertyGridForm>;
  getValues: UseFormGetValues<PropertyGridForm>;
  setValue: UseFormSetValue<PropertyGridForm>;
  unitRateModels?: UnitRateModels | null;
  sourceDescription?: string;
};

const FlexItem = ({
  children,
  backgroundColor = 'surfaceAccent7',
  color = 'textOffset',
  fullWidth,
  justifyContent = 'space-between',
}: PropsWithChildren<{
  backgroundColor?: ProductColor;
  color?: ProductColor;
  fullWidth?: boolean;
  justifyContent?: FlexProps['justifyContent'];
}>) => {
  const subHeaderFlexProps: FlexProps = {
    height: PROPERTY_GRID_ROW_HEIGHT,
    padding: '4px 8px',
    alignItems: 'center',
    justifyContent,
    gridColumn: fullWidth ? '1 / -1' : 'span 1',
    backgroundColor,
    __dangerDoNotUseSx: {
      color: theme.colors[color],
    },
  };

  return <Flex {...subHeaderFlexProps}>{children}</Flex>;
};

const LabelItem = ({
  children,
  backgroundColor = 'surfaceAccent7',
  color = 'textOffset',
}: {
  children: TypographyChildren;
  backgroundColor?: ProductColor;
  color?: ProductColor;
  gridColumn?: SystemStyleObject['gridColumn'];
}) => {
  const typographyProps: TypographyProps = {
    variant: 'body2',
    color,
    __dangerDoNotUseSx: {
      height: PROPERTY_GRID_ROW_HEIGHT,
      paddingLeft: '4px',
      alignContent: 'center',
      backgroundColor: theme.colors[backgroundColor],
      whiteSpace: 'nowrap',
    },
  };

  return <Typography {...typographyProps}>{children}</Typography>;
};

const CompTypeBadge = ({ compType }: { compType: CompType }) => (
  <Badge backgroundColor={compType} size="sm" textColor={`${compType}Text`} borderRadius="4px">
    {compType}
  </Badge>
);

const getCompTypeColors = (
  compType: CompType,
  hasArvOption: boolean,
): { backgroundColor: ProductColor; color: ProductColor } => ({
  backgroundColor: hasArvOption ? (`${compType}Light` as ProductColor) : 'surfaceAccent7',
  color: hasArvOption ? 'textPrimary' : 'textOffset',
});

const getDataSourceWarningMessage = (
  type: SourceType,
  unitRateModels: UnitRateModels | null | undefined,
): string | null => {
  if (type === 'sensitivity') return null;

  if (type === 'regression') {
    const regression = unitRateModels?.regression;
    if (!regression || regression?.status === 'error') return null;

    if (!regression.output.modelDiagnostics.reliable) {
      return getRegressionModelUnreliableReason(unitRateModels);
    }
  }

  return null;
};

const DataSourceItem = ({
  subLabel,
  infoIconProps,
}: {
  subLabel: string;
  infoIconProps?: InfoIconProps;
}) => (
  <FlexItem backgroundColor="surfaceAccent5" color="textPrimary" fullWidth>
    <Tooltip label={subLabel} textAlign="center">
      <Typography variant="h5" truncate __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}>
        {subLabel}
      </Typography>
    </Tooltip>
    {infoIconProps && <InfoIcon {...infoIconProps} />}
  </FlexItem>
);
const FeatureItem = ({ compType, hasArvOption }: { compType: CompType; hasArvOption: boolean }) => {
  const { backgroundColor, color } = getCompTypeColors(compType, hasArvOption);

  return (
    <FlexItem backgroundColor={backgroundColor} color={color}>
      <LabelItem backgroundColor={backgroundColor} color={color}>
        Feature
      </LabelItem>
      {hasArvOption && <CompTypeBadge compType={compType} />}
    </FlexItem>
  );
};

const SimilarityScoreItem = ({
  compType,
  hasArvOption,
  similarityScore,
}: {
  compType: CompType;
  hasArvOption: boolean;
  similarityScore?: string;
}) => {
  const { backgroundColor, color } = getCompTypeColors(compType, hasArvOption);

  return (
    <FlexItem backgroundColor={backgroundColor} color={color} fullWidth>
      <LabelItem backgroundColor={backgroundColor} color={color}>
        Similarity Score:&nbsp;
        {similarityScore ?? '-'}
      </LabelItem>
      {hasArvOption && <CompTypeBadge compType={compType} />}
    </FlexItem>
  );
};

const UnitRateItem = () => <LabelItem>Unit Rate</LabelItem>;

const AdjustmentItem = ({
  compType,
  hasArvOption,
  hasCustomManualAdjustments,
  columnIndex,
  control,
  getValues,
  setValue,
}: Pick<PropertyGridColumnSubHeaderProps, 'columnIndex' | 'control' | 'getValues' | 'setValue'> & {
  compType: CompType;
  hasArvOption: boolean;
  hasCustomManualAdjustments: boolean;
}) => {
  const { backgroundColor, color } = getCompTypeColors(compType, hasArvOption);

  return (
    <FlexItem backgroundColor={backgroundColor} color={color}>
      <LabelItem backgroundColor={backgroundColor} color={color}>
        Adjustment
      </LabelItem>
      {hasCustomManualAdjustments && (
        <ResetAdjustmentsButton
          columnIndex={columnIndex}
          control={control}
          getValues={getValues}
          setValue={setValue}
        />
      )}
    </FlexItem>
  );
};

export const LabelColumnSubHeader = ({ gridType }: { gridType: PropertyGridType }) => {
  if (gridType === 'SensitivityGrid') return null;
  return <LabelItem>Feature</LabelItem>;
};

export const PropertyGridColumnSubHeader = ({
  gridType,
  columnIndex,
  control,
  getValues,
  setValue,
  unitRateModels,
  sourceDescription,
}: PropertyGridColumnSubHeaderProps) => {
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const salesComparisonApproach = useAppSelector(selectSalesComparisonApproach);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const { showAivComps, showArvComps } = useAppSelector(selectCompTypeVisibility);
  const subject = useAppSelector((state) =>
    selectPropertyDataById(state, PROPERTY_GRID_SUBJECT_PROPERTY),
  ) as SubjectPropertyDataState | undefined;

  const columnData = useWatch({ control, name: `columns.${columnIndex}` });
  const manualAdjustments = useWatch({
    control,
    name: `columns.${columnIndex}.propertyData.editableData.manualAdjustments`,
  });

  const { propertyId, type } = columnData ?? {};
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, propertyId));
  const propertyData = isSubject
    ? (propertyDataState as SubjectPropertyDataState | undefined)?.subjectData
    : (propertyDataState as CompPropertyDataState | undefined)?.compData;

  const compType: CompType = useAppSelector((state) => selectCompType(state, propertyId)) ?? 'aiv';

  const hasCustomManualAdjustments = propertyHasCustomManualAdjustments(manualAdjustments);

  const isFeatureGrid = gridType === 'FeatureGrid';
  const isSensitivityGrid = gridType === 'SensitivityGrid';
  if (!subject || !effectiveDate || !propertyData || isSensitivityGrid) return null;

  if (type !== 'property') {
    const warningMessage = getDataSourceWarningMessage(type, unitRateModels);
    let subLabel: string = getDataSourceDisplayInfo(type).text;

    if (gridType === 'FeatureGrid') {
      if (sourceDescription) subLabel = sourceDescription;
    } else if (gridType === 'AdjustmentsGrid') {
      if (type === 'regression' && unitRateModels?.regression?.status === 'success') {
        subLabel = `Sample Size: ${unitRateModels.regression.output.sampleSize}`;
      } else if (type === 'sensitivity' && unitRateModels?.sensitivityAnalysis) {
        const testPoints = defaultUnitRateModelFeatureKeys.reduce(
          (sum, key) =>
            sum + (unitRateModels.sensitivityAnalysis![key]?.testedUnitRates.length ?? 0),
          0,
        );

        if (testPoints > 0) subLabel = `Test Points: ${testPoints}`;
      }
    }

    return (
      <DataSourceItem
        subLabel={subLabel}
        infoIconProps={
          warningMessage ? { tooltipContent: warningMessage, color: 'alertWarning' } : undefined
        }
      />
    );
  }

  if (isSubject) {
    const featureItems = [];

    if (!hasArvOption) {
      featureItems.push(<FeatureItem key="aiv" hasArvOption={hasArvOption} compType={compType} />);
    } else {
      if (showAivComps) {
        featureItems.push(<FeatureItem key="aiv" hasArvOption={hasArvOption} compType="aiv" />);
      }
      if (showArvComps) {
        featureItems.push(<FeatureItem key="arv" hasArvOption={hasArvOption} compType="arv" />);
      }
    }

    return (
      <>
        {featureItems}
        {salesComparisonApproach === 'unit_rate' && !isFeatureGrid && <UnitRateItem />}
      </>
    );
  }

  if (isFeatureGrid) {
    const similarityScore = !isSubject
      ? getSimilarityScore(
          subject.subjectData,
          effectiveDate,
          propertyId,
          propertyData as PropertyData,
        )
      : undefined;

    return (
      <SimilarityScoreItem
        compType={compType}
        hasArvOption={hasArvOption}
        similarityScore={similarityScore}
      />
    );
  }

  return (
    <>
      <FeatureItem hasArvOption={hasArvOption} compType={compType} />
      <AdjustmentItem
        hasCustomManualAdjustments={hasCustomManualAdjustments}
        hasArvOption={hasArvOption}
        compType={compType}
        columnIndex={columnIndex}
        control={control}
        getValues={getValues}
        setValue={setValue}
      />
    </>
  );
};
